/**
 * useOnboarding Hook
 * 
 * Manages onboarding state: checking if completed and marking as complete.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { OnboardingType } from "../types";

interface UseOnboardingReturn {
  /** Whether the onboarding has been completed */
  isCompleted: boolean;
  /** Whether we're still loading the status */
  isLoading: boolean;
  /** Error message if something went wrong */
  error: string | null;
  /** Mark onboarding as complete */
  completeOnboarding: () => Promise<void>;
  /** Refresh the onboarding status */
  refresh: () => Promise<void>;
}

/**
 * Profile with onboarding columns
 */
interface OnboardingProfile {
  onboarding_completed_at: string | null;
  admin_onboarding_completed_at: string | null;
}

/**
 * Hook to manage onboarding state
 * 
 * @param type - The type of onboarding (user or admin)
 * @returns Onboarding state and actions
 */
export const useOnboarding = (type: OnboardingType): UseOnboardingReturn => {
  const { user } = useAuth();
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const columnName: keyof OnboardingProfile = type === "user" 
    ? "onboarding_completed_at" 
    : "admin_onboarding_completed_at";

  /**
   * Fetch onboarding status from database
   */
  const fetchStatus = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("onboarding_completed_at, admin_onboarding_completed_at")
        .eq("id", user.id)
        .single();

      if (fetchError) {
        // Column might not exist yet, treat as not completed
        if (fetchError.message.includes("column") || fetchError.code === "42703") {
          setIsCompleted(false);
        } else {
          throw fetchError;
        }
      } else if (data) {
        const profileData = data as OnboardingProfile;
        setIsCompleted(!!profileData[columnName]);
      } else {
        setIsCompleted(false);
      }
    } catch (err) {
      console.error("Error fetching onboarding status:", err);
      setError("Kon onboarding status niet ophalen");
      setIsCompleted(false);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, columnName]);

  /**
   * Mark onboarding as complete
   */
  const completeOnboarding = useCallback(async () => {
    if (!user?.id) return;

    try {
      setError(null);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ [columnName]: new Date().toISOString() })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Log completion to console (activity log types don't include onboarding)
      console.log(`[Onboarding] ${type} onboarding completed for user ${user.id}`);

      setIsCompleted(true);
    } catch (err) {
      console.error("Error completing onboarding:", err);
      setError("Kon onboarding niet voltooien");
      throw err;
    }
  }, [user?.id, columnName, type]);

  /**
   * Refresh status
   */
  const refresh = useCallback(async () => {
    await fetchStatus();
  }, [fetchStatus]);

  // Fetch status on mount and when user changes
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    isCompleted,
    isLoading,
    error,
    completeOnboarding,
    refresh,
  };
};
